<template>
  <div class="orders-table">
    <b-table
      class="custom-table"
      borderless
      responsive
      :busy="isBusy"
      :fields="fields"
      :items="sortedOrders">
      <template #table-busy>
        <div class="orders-table__loading">
          <font-awesome-icon icon="spinner"
                             size="2x"
                             spin>
          </font-awesome-icon>
          <strong>Loading order history...</strong>
        </div>
      </template>
      <template v-slot:cell(details)="row">
        <div class="orders-table__actions">
          <button
            class="btn btn-sm btn-accent-1 btnOrderView"
            @click="viewOrderHistoryTransactions(row)">
            <template v-if="row.item.loading">
              <b-spinner class="align-middle"></b-spinner>
            </template>
            <template v-else>
              {{ row.detailsShowing ? "Hide" : "View" }}
            </template>
          </button>
          <button
            v-if="row.item.showRefundOption"
            class="btn btn-sm btn-accent-1 btnOrderRefund"
            @click="onRefundClick(row.item.orderHeaderId)">
            {{ "Refund" }}
          </button>
        </div>
      </template>
      <template v-slot:row-details="row">
        <div class="orders-table__card">
          <div class="orders-table__card-header">
            Products
          </div>
          <!-- original order -->
          <div v-if="!row.item.loaded"
               class="orders-table__card-loading">
            <h6>Loading order details...</h6>
            <font-awesome-icon icon="spinner"
                               class="icon"
                               size="lg"
                               spin></font-awesome-icon>
          </div>
          <template v-else>
            <div
              v-for="(item, index) in row.item.itemsInOrder"
              :key="index"
              class="orders-table__card-line">
              <div
                :class="[
                  'row',
                  'mx-0',
                  {
                    'orders-table__card-line-transaction':
                      Array.isArray(item.transactions) && item.transactions.length > 0
                  }
                ]">
                <div class="col-lg-7 col-8 no-padding">
                  {{ item.productName }}
                </div>
                <div class="col-lg-2 col-2 no-padding">
                  x{{ item.quantity }}
                </div>
                <div class="col-lg-1 col-2 no-padding">
                  {{ formatPrice(item.lineValue) }}
                </div>
                <div class="col-lg-2 col-12 no-padding">
                  <button
                    v-if="item.statusId == 'ACT'"
                    class="btn btn-sm btn-accent-2 orderlineStatus btnOrderLineRefund"
                    @click="
                      onRefundLineClick(
                        row.item.orderHeaderId,
                        item.creditOrderIds,
                        item.creditOrderLineIds
                      )
                    ">
                    {{ item.statusText }}
                  </button>

                  <span v-if="item.statusId == 'SUB'"
                        class="orderlineStatus">{{
                          item.statusText
                        }}</span>
                </div>
              </div>
              <!-- credit history -->
              <div v-for="(trx, i) in item.transactions"
                   :key="i"
                   class="row no-padding">
                <div class="col-lg-7 col-8 no-padding">
                  {{ row.item.itemsInOrder[index].productName }}
                </div>
                <div class="col-lg-2 col-2 no-padding">
                  {{ trx.refundQuantity }}/{{ trx.quantity }}
                </div>
                <div class="col-lg-1 col-2 no-padding">
                  -{{ formatPrice(trx.totalLinePrice) }}
                </div>
                <div class="col-lg-2 col-12 no-padding">
                  <button
                    v-if="item.statusId == 'ACT'"
                    class="btn btn-sm btn-accent-2 orderlineStatus btnOrderLineRefund"
                    @click="
                      onRefundLineClick(
                        row.item.orderHeaderId,
                        item.creditOrderIds,
                        item.creditOrderLineIds
                      )
                    ">
                    {{ item.statusText }}
                  </button>

                  <span v-if="trx.refundStatusId == 'SUB'"
                        class="orderlineStatus">{{
                          trx.transactionStatus
                        }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </template>
    </b-table>

    <OrderRefundsModal
      :show="showOrderRefundsModal"
      :order-id="orderId"
      @close="showOrderRefundsModal = false"
      @refreshPage="refreshPage"></OrderRefundsModal>

    <OrderLineRefundModal
      :show="showOrderLineRefundModal"
      :order-id="orderId"
      :credit-order-id="creditOrderId"
      :credit-order-line-id="creditOrderLineId"
      @close="showOrderLineRefundModal = false"
      @refreshPage="refreshPage"></OrderLineRefundModal>
  </div>
</template>

<script>
import { format } from "date-fns";
import { EcommerceModule } from "spiritweb.packages.ecommerce";
import OrderRefundsModal from "~/Components/OrdersHistory/OrderRefundsModal.vue";
import OrderLineRefundModal from "~/Components/OrdersHistory/OrderLineRefundModal.vue";
import { getOrderLinesTransactionHistory } from "~/Scripts/resources/ordersResource";

export default {
  name: "OrdersTable",
  components: {
    OrderRefundsModal,
    OrderLineRefundModal
  },
  props: {
    orders: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      isBusy: false,
      fields: [
        {
          key: "orderNumber",
          label: "#"
        },
        {
          key: "dateOrdered",
          label: "Date ordered",
          formatter: this.formatDate
        },
        {
          key: "orderedBy",
          label: "Purchased by"
        },
        {
          key: "orgName",
          label: "Organisation"
        },
        {
          key: "purchaseOrderNo",
          label: "PO"
        },
        {
          key: "itemsInOrder",
          label: "Items in order",
          formatter: this.formatItems
        },
        {
          key: "totalCost",
          label: "Total cost (exc. vat)",
          formatter: this.formatPrice
        },
        {
          key: "details",
          label: ""
        }
      ],
      showOrderRefundsModal: false,
      showOrderLineRefundModal: false,
      orderId: null,
      orderLineId: null,
      creditOrderId: null,
      creditOrderLineId: null
    };
  },
  computed: {
    sortedOrders () {
      const result = [...this.orders];
      result.sort( ( a, b ) => new Date( b.dateOrdered ) - new Date( a.dateOrdered ) );
      return result;
    }
  },
  methods: {
    async viewOrderHistoryTransactions ( row ) {
      row.toggleDetails();

      if ( !row.detailsShowing ) {
        const {
          item: { itemsInOrder }
        } = row;

        try {
          this.$set( row.item, "loaded", false );
          const results = await getOrderLinesTransactionHistory( itemsInOrder );
          row.item.itemsInOrder = results.data.map( r => ( {
            ...r,
            transactions: r.transactions?.filter( trx => trx.creditOrderLineId ) || []
          } ) );
        } catch ( err ) {
          console.warn( "Failed retrieving transaction history." );
          console.error( err );
        } finally {
          this.$set( row.item, "loaded", true );
        }
      }
    },
    formatDate ( input ) {
      return format( new Date( input ), "dd/MM/yyyy" );
    },
    formatItems ( input ) {
      let result = "";

      if ( input && input.length > 0 ) {
        result += `${input.length} item${input.length !== 1 ? "s" : ""}: `;
        result += input.map( x => x.productName ).join( ", " );
      }

      if ( result.length > 45 ) {
        result = `${result.substring( 0, 45 )}...`;
      }

      return result;
    },
    formatPrice ( input ) {
      return EcommerceModule.renderPrice( input, "gbp", "en-gb" );
    },
    onRefundClick ( id ) {
      this.orderId = id;
      this.showOrderRefundsModal = true;
    },
    onRefundLineClick ( orderId, creditOrderIds, creditOrderLineIds ) {
      this.orderId = orderId;
      // todo: one to many credit orders
      [this.creditOrderId] = creditOrderIds;
      [this.creditOrderLineId] = creditOrderLineIds;
      this.showOrderLineRefundModal = true;
    },
    refreshPage () {
      this.showOrderRefundsModal = false;
      this.showOrderLineRefundModal = false;
      this.$emit( "refreshPage" );
    }
  }
};
</script>

<style lang="less">
@import "../../Styles/utils/index";

.orders-table {
  background-color: #fff;

  &__loading {
    color: #007a7f;
    height: 200px;
    display: flex;
    justify-content: center;
    align-items: center;

    > * + * {
      margin-left: 0.5rem;
    }
  }

  &__card-loading {
    padding: 20px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  &__card {
    border: 2px solid #b2d6d9;
    background-color: #fff;
    padding: 10px;

    &-header {
      font-size: 1.2rem;
      font-weight: 600;
    }

    &-line + .orders-table__card-line {
      border-top: 1px solid #b2d6d9;
    }

    &-line {
      padding: 5px;
    }

    &-line-transaction {
      padding-bottom: 0.5rem;
      margin-bottom: 0.5rem;
      border-bottom: 1px solid #dee2e6 !important;
    }
  }

  &__actions {
    display: flex;

    > button {
      .babcock-table-action();
    }
  }

  .no-padding {
    margin: 0 !important;
    padding: 0 !important;
  }

  .btnOrderRefund {
    margin-left: 5px;
  }

  .btnOrderLineRefund {
    background-color: #78b7bc;
  }

  .orderlineStatus {
    float: right;
  }
}
</style>
